import { useEffect, useRef } from "react";
import { Link } from "react-router-dom";
import { motion, useInView } from "framer-motion";
import { ArrowUpRight, TrendingUp, Users, ArrowUpRight as Growth, Clock, MapPin, DollarSign } from "lucide-react";
import CTABand from "@/components/recruit/CTABand";

function AnimatedStat({ value, unit, label, delay = 0 }) {
  const ref = useRef(null);
  const isInView = useInView(ref, { once: true, margin: "-50px" });

  return (
    <motion.div
      ref={ref}
      initial={{ opacity: 0, y: 20 }}
      animate={isInView ? { opacity: 1, y: 0 } : { opacity: 0, y: 20 }}
      transition={{ duration: 0.6, delay, ease: [0.25, 0.1, 0.25, 1] }}
      className="text-center"
    >
      <p className="font-heading font-extrabold text-3xl md:text-4xl text-acoe-charcoal">
        {value}<span className="text-lg text-acoe-gold ml-1">{unit}</span>
      </p>
      <p className="mt-1 text-sm text-acoe-muted">{label}</p>
    </motion.div>
  );
}

const QUICK_FACTS = [
  { value: "102", unit: "室", label: "運営客室数" },
  { value: "4.96", unit: "", label: "Airbnb評価" },
  { value: "96.9", unit: "%", label: "品質スコア" },
  { value: "7", unit: "年", label: "運営実績" },
];

const JOB_HIGHLIGHTS = [
  { icon: DollarSign, title: "500-800万 + インセンティブ", desc: "上限なしのインセンティブ制度" },
  { icon: MapPin, title: "リモート中心", desc: "出社週２回・フレックス勤務" },
  { icon: Clock, title: "年間休日120日", desc: "月平均残業20h以内" },
  { icon: Growth, title: "キャリアパス", desc: "役員登用の可能性" },
];

const WHY_POINTS = [
  { icon: TrendingUp, title: "勝てるプロダクト", desc: "Airbnb 4.96、品質スコア96.9%。実績で証明された運営力で提案できる。" },
  { icon: Users, title: "柔軟な働き方", desc: "リモート中心、フレキシブルな勤務時間。成果に集中できる環境。" },
  { icon: Growth, title: "見える成長パス", desc: "新規事業フェーズ。成果は報酬と役職に直結。" },
];

export default function SummaryPage() {
  useEffect(() => { document.title = "5分でわかるACOE | ACOE 採用サイト"; }, []);

  return (
    <div data-testid="summary-page">
      {/* ─── HERO ─── */}
      <section className="pt-32 md:pt-40 pb-16 md:pb-24 bg-acoe-charcoal" data-testid="summary-hero">
        <div className="max-w-[1400px] mx-auto px-6 md:px-12">
          <p className="text-sm tracking-[0.25em] uppercase text-acoe-gold font-heading font-semibold mb-6">Quick Summary</p>
          <h1 className="font-heading font-extrabold text-4xl sm:text-5xl lg:text-6xl text-white leading-[1.15] max-w-3xl">
            5分でわかるACOE
          </h1>
          <p className="mt-6 text-white/60 text-base md:text-lg max-w-2xl leading-relaxed">
            忙しい方向けに、ACOEとこのポジションのエッセンスをまとめました。
          </p>
        </div>
      </section>

      {/* ─── QUICK FACTS ─── */}
      <section className="py-12 bg-acoe-bg border-b border-acoe-border" data-testid="summary-facts">
        <div className="max-w-[1400px] mx-auto px-6 md:px-12">
          <div className="grid grid-cols-2 md:grid-cols-4 gap-6 md:gap-8">
            {QUICK_FACTS.map((fact, i) => (
              <AnimatedStat key={i} {...fact} delay={i * 0.1} />
            ))}
          </div>
        </div>
      </section>

      {/* ─── WHAT IS ACOE ─── */}
      <section className="py-16 md:py-24 bg-acoe-bg" data-testid="summary-what">
        <div className="max-w-[1400px] mx-auto px-6 md:px-12">
          <div className="max-w-3xl">
            <p className="text-sm tracking-[0.25em] uppercase text-acoe-muted font-heading font-semibold mb-3">What is ACOE</p>
            <h2 className="font-heading font-bold text-2xl md:text-3xl text-acoe-charcoal mb-6">
              ACOEとは
            </h2>
            <p className="text-acoe-text leading-relaxed mb-4">
              ACOEは、東京でファミリー向けライフスタイル宿泊ブランドを展開する会社です。
              一般的な民泊より高品質で、ホテルよりスペースがあり、完全にプライベートな空間。
              7年間の運営実績でブランド力と運営ノウハウを蓄積してきました。
            </p>
            <Link
              to="/recruit/about"
              className="inline-flex items-center gap-2 text-acoe-charcoal font-heading font-semibold hover:text-acoe-gold transition-colors group"
            >
              詳しく見る
              <ArrowUpRight size={18} className="group-hover:translate-x-0.5 group-hover:-translate-y-0.5 transition-transform" strokeWidth={1.5} />
            </Link>
          </div>
        </div>
      </section>

      {/* ─── THE ROLE ─── */}
      <section className="py-16 md:py-24 bg-acoe-paper" data-testid="summary-role">
        <div className="max-w-[1400px] mx-auto px-6 md:px-12">
          <div className="max-w-3xl">
            <p className="text-sm tracking-[0.25em] uppercase text-acoe-muted font-heading font-semibold mb-3">The Role</p>
            <h2 className="font-heading font-bold text-2xl md:text-3xl text-acoe-charcoal mb-6">
              物件開拓営業とは
            </h2>
            <p className="text-acoe-text leading-relaxed mb-6">
              ACOEブランドで運営できる物件を見つけ、増やしていくポジションです。
              物件ソーシング、オーナーへの提案、契約締結、その後のリレーション構築まで。
              一つひとつの契約が、ブランドの成長そのものになります。
            </p>
            <div className="grid grid-cols-2 gap-4 mb-6">
              {JOB_HIGHLIGHTS.map((item, i) => {
                const Icon = item.icon;
                return (
                  <div key={i} className="flex items-start gap-3">
                    <Icon size={18} className="text-acoe-gold mt-0.5 flex-shrink-0" strokeWidth={1.5} />
                    <div>
                      <p className="font-heading font-semibold text-sm text-acoe-charcoal">{item.title}</p>
                      <p className="text-xs text-acoe-muted">{item.desc}</p>
                    </div>
                  </div>
                );
              })}
            </div>
            <Link
              to="/recruit/work"
              className="inline-flex items-center gap-2 text-acoe-charcoal font-heading font-semibold hover:text-acoe-gold transition-colors group"
            >
              仕事の詳細を見る
              <ArrowUpRight size={18} className="group-hover:translate-x-0.5 group-hover:-translate-y-0.5 transition-transform" strokeWidth={1.5} />
            </Link>
          </div>
        </div>
      </section>

      {/* ─── WHY ACOE ─── */}
      <section className="py-16 md:py-24 bg-acoe-bg" data-testid="summary-why">
        <div className="max-w-[1400px] mx-auto px-6 md:px-12">
          <p className="text-sm tracking-[0.25em] uppercase text-acoe-muted font-heading font-semibold mb-3">Why ACOE</p>
          <h2 className="font-heading font-bold text-2xl md:text-3xl text-acoe-charcoal mb-8">
            なぜACOEか
          </h2>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
            {WHY_POINTS.map((item, i) => {
              const Icon = item.icon;
              return (
                <div key={i} className="bg-white border border-acoe-border p-6">
                  <Icon size={20} className="text-acoe-gold mb-3" strokeWidth={1.5} />
                  <h3 className="font-heading font-bold text-base text-acoe-charcoal mb-2">{item.title}</h3>
                  <p className="text-sm text-acoe-text leading-relaxed">{item.desc}</p>
                </div>
              );
            })}
          </div>
        </div>
      </section>

      {/* ─── HIRING PROCESS MINI ─── */}
      <section className="py-16 md:py-24 bg-acoe-charcoal" data-testid="summary-process">
        <div className="max-w-[1400px] mx-auto px-6 md:px-12">
          <p className="text-sm tracking-[0.25em] uppercase text-acoe-gold font-heading font-semibold mb-3">Hiring Process</p>
          <h2 className="font-heading font-bold text-2xl md:text-3xl text-white mb-8">
            選考の流れ
          </h2>
          <div className="flex flex-col md:flex-row md:items-center gap-4 md:gap-6">
            {[
              { step: "01", label: "カジュアル面談" },
              { step: "02", label: "適性検査" },
              { step: "03", label: "面接 1-2回" },
              { step: "04", label: "内定・入社" },
            ].map((item, i, arr) => (
              <div key={i} className="flex items-center gap-4">
                <div className="flex items-center gap-3">
                  <span className="font-heading font-extrabold text-2xl text-acoe-gold/30">{item.step}</span>
                  <span className="text-white font-heading font-semibold">{item.label}</span>
                </div>
                {i < arr.length - 1 && (
                  <ArrowUpRight size={16} className="text-acoe-gold/40 hidden md:block rotate-45" strokeWidth={2} />
                )}
              </div>
            ))}
          </div>
          <p className="mt-6 text-sm text-white/40">カジュアル面談～内定まで最短2週間</p>
          <Link
            to="/recruit/process"
            className="inline-flex items-center gap-2 text-white font-heading font-semibold hover:text-acoe-gold transition-colors group mt-6"
          >
            詳しく見る
            <ArrowUpRight size={18} className="group-hover:translate-x-0.5 group-hover:-translate-y-0.5 transition-transform" strokeWidth={1.5} />
          </Link>
        </div>
      </section>

      {/* ─── NEXT STEPS ─── */}
      <section className="py-16 md:py-24 bg-acoe-paper" data-testid="summary-next">
        <div className="max-w-[1400px] mx-auto px-6 md:px-12">
          <p className="text-sm tracking-[0.25em] uppercase text-acoe-muted font-heading font-semibold mb-3">Explore More</p>
          <h2 className="font-heading font-bold text-2xl md:text-3xl text-acoe-charcoal mb-8">
            もっと知りたい方へ
          </h2>
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
            {[
              { title: "ACOEについて", path: "/recruit/about", desc: "事業・実績・成り立ち" },
              { title: "仕事について", path: "/recruit/work", desc: "具体的な仕事内容" },
              { title: "制度・環境", path: "/recruit/culture", desc: "働き方・評価・成長" },
              { title: "募集要項", path: "/recruit/careers", desc: "詳細な条件・FAQ" },
            ].map((item, i) => (
              <Link
                key={i}
                to={item.path}
                className="block bg-white border border-acoe-border p-6 hover:border-acoe-charcoal transition-all duration-300 group"
              >
                <h3 className="font-heading font-bold text-base text-acoe-charcoal mb-1 group-hover:text-acoe-gold transition-colors">{item.title}</h3>
                <p className="text-sm text-acoe-muted">{item.desc}</p>
                <ArrowUpRight size={16} className="mt-3 text-acoe-muted group-hover:text-acoe-charcoal group-hover:translate-x-0.5 group-hover:-translate-y-0.5 transition-all" strokeWidth={1.5} />
              </Link>
            ))}
          </div>
        </div>
      </section>

      {/* ─── CTA BAND ─── */}
      <CTABand variant="dark" />
    </div>
  );
}
